// Gmail API service for scanning and importing boarding passes
export interface GmailAttachment {
  messageId: string;
  attachmentId: string;
  filename: string;
  mimeType: string;
  size: number;
  from: string;
  subject: string;
  date: string;
}

export interface ImportableDocument extends GmailAttachment {
  category: 'boarding-pass' | 'hotel' | 'insurance' | 'id-passport' | 'payment' | 'other';
  preview?: string;
}

const GMAIL_API_BASE = 'https://gmail.googleapis.com/gmail/v1/users/me';

// Search queries for travel-related emails
const TRAVEL_QUERIES = [
  'subject:(boarding pass OR flight confirmation OR ticket)',
  'from:(@aa.com OR @delta.com OR @united.com OR @southwest.com OR @jetblue.com OR @alaskaair.com OR @spirit.com)',
  'subject:(hotel confirmation OR reservation confirmed)',
  'from:(@marriott.com OR @hilton.com OR @hyatt.com OR @ihg.com OR @airbnb.com)',
  'filename:pdf (boarding OR flight OR ticket OR hotel OR reservation)'
];

/**
 * Request Gmail API access with additional scopes
 */
export async function requestGmailAccess(): Promise<string> {
  // This will need to be integrated with your existing Supabase Google OAuth
  // For now, we'll return a placeholder
  // You'll need to update the Google OAuth scopes in Supabase dashboard to include:
  // - https://www.googleapis.com/auth/gmail.readonly
  // - https://www.googleapis.com/auth/gmail.metadata
  
  throw new Error('Gmail access must be configured in Supabase Google OAuth settings first');
}

/**
 * Scan Gmail inbox for boarding passes and travel documents
 */
export async function scanGmailForTravelDocs(accessToken: string): Promise<ImportableDocument[]> {
  const allDocuments: ImportableDocument[] = [];
  
  try {
    // Search for messages matching travel queries
    for (const query of TRAVEL_QUERIES) {
      const searchUrl = `${GMAIL_API_BASE}/messages?q=${encodeURIComponent(query)}&maxResults=50`;
      
      const response = await fetch(searchUrl, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        console.error('Gmail API search failed:', response.status);
        continue;
      }
      
      const data = await response.json();
      const messageIds = data.messages?.map((m: any) => m.id) || [];
      
      // Fetch full message details including attachments
      for (const messageId of messageIds) {
        const messageUrl = `${GMAIL_API_BASE}/messages/${messageId}`;
        const msgResponse = await fetch(messageUrl, {
          headers: {
            'Authorization': `Bearer ${accessToken}`
          }
        });
        
        if (msgResponse.ok) {
          const message = await msgResponse.json();
          const documents = extractDocumentsFromMessage(message);
          allDocuments.push(...documents);
        }
      }
    }
    
    // Remove duplicates by attachmentId
    const unique = allDocuments.filter((doc, index, self) =>
      index === self.findIndex((d) => d.attachmentId === doc.attachmentId)
    );
    
    return unique;
  } catch (error) {
    console.error('Error scanning Gmail:', error);
    throw error;
  }
}

/**
 * Extract attachments from a Gmail message
 */
function extractDocumentsFromMessage(message: any): ImportableDocument[] {
  const documents: ImportableDocument[] = [];
  
  // Get message metadata
  const headers = message.payload?.headers || [];
  const from = headers.find((h: any) => h.name === 'From')?.value || 'Unknown';
  const subject = headers.find((h: any) => h.name === 'Subject')?.value || 'No Subject';
  const date = headers.find((h: any) => h.name === 'Date')?.value || message.internalDate;
  
  // Check for attachments
  const parts = message.payload?.parts || [];
  
  for (const part of parts) {
    if (part.filename && part.body?.attachmentId) {
      const doc: ImportableDocument = {
        messageId: message.id,
        attachmentId: part.body.attachmentId,
        filename: part.filename,
        mimeType: part.mimeType,
        size: part.body.size,
        from,
        subject,
        date,
        category: categorizeTravelEmail(from, subject, part.filename)
      };
      
      documents.push(doc);
    }
  }
  
  return documents;
}

/**
 * Categorize email based on sender and subject
 */
function categorizeTravelEmail(from: string, subject: string, filename: string): ImportableDocument['category'] {
  const lowerFrom = from.toLowerCase();
  const lowerSubject = subject.toLowerCase();
  const lowerFilename = filename.toLowerCase();
  
  // Airlines
  if (lowerFrom.includes('aa.com') || lowerFrom.includes('delta.com') || 
      lowerFrom.includes('united.com') || lowerFrom.includes('southwest.com') ||
      lowerFrom.includes('jetblue.com') || lowerFrom.includes('alaskaair.com') ||
      lowerSubject.includes('boarding') || lowerSubject.includes('flight')) {
    return 'boarding-pass';
  }
  
  // Hotels
  if (lowerFrom.includes('marriott.com') || lowerFrom.includes('hilton.com') ||
      lowerFrom.includes('hyatt.com') || lowerFrom.includes('airbnb.com') ||
      lowerSubject.includes('hotel') || lowerSubject.includes('reservation')) {
    return 'hotel';
  }
  
  // Insurance
  if (lowerSubject.includes('insurance') || lowerSubject.includes('policy')) {
    return 'insurance';
  }
  
  return 'other';
}

/**
 * Download attachment from Gmail
 */
export async function downloadGmailAttachment(
  accessToken: string,
  messageId: string,
  attachmentId: string
): Promise<string> {
  const url = `${GMAIL_API_BASE}/messages/${messageId}/attachments/${attachmentId}`;
  
  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${accessToken}`
    }
  });
  
  if (!response.ok) {
    throw new Error(`Failed to download attachment: ${response.status}`);
  }
  
  const data = await response.json();
  
  // Gmail returns base64url encoded data, convert to standard base64
  const base64 = data.data.replace(/-/g, '+').replace(/_/g, '/');
  
  return `data:application/pdf;base64,${base64}`;
}
